/* eslint-disable react-hooks/refs */
import { useCallback, useEffect, useRef } from 'react'
import { logger } from '../utils/logger'

/** DOM events that count as user activity for session inactivity reset. */
const ACTIVITY_EVENTS = [
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'click',
  'wheel',
] as const

/** Minimum interval between timer resets (mousemove omitted to avoid noise). */
const ACTIVITY_THROTTLE_MS = 1000

export interface UseSessionInactivityLogoutOptions {
  /** When false or timeoutMs <= 0, the hook is a no-op. */
  enabled: boolean
  /** Idle duration in milliseconds before onTimeout fires. */
  timeoutMs: number
  /** Called once when the inactivity period elapses. */
  onTimeout: () => void
}

/**
 * Logs the user out after a period without user interaction.
 * Resets the timer on common input events and re-checks elapsed idle time when the tab becomes visible.
 */
export function useSessionInactivityLogout({
  enabled,
  timeoutMs,
  onTimeout,
}: UseSessionInactivityLogoutOptions): void {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // eslint-disable-next-line react-hooks/purity
  const lastActivityRef = useRef(Date.now())
  const lastThrottleRef = useRef(0)
  const onTimeoutRef = useRef(onTimeout)
  onTimeoutRef.current = onTimeout

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const scheduleTimer = useCallback(() => {
    clearTimer()
    if (!enabled || timeoutMs <= 0) return
    timeoutRef.current = setTimeout(() => {
      logger.warn('Session inactivity timeout reached')
      onTimeoutRef.current()
    }, timeoutMs)
  }, [clearTimer, enabled, timeoutMs])

  const recordActivity = useCallback(() => {
    lastActivityRef.current = Date.now()
    scheduleTimer()
  }, [scheduleTimer])

  const handleActivity = useCallback(() => {
    const now = Date.now()
    if (now - lastThrottleRef.current < ACTIVITY_THROTTLE_MS) return
    lastThrottleRef.current = now
    recordActivity()
  }, [recordActivity])

  useEffect(() => {
    if (!enabled || timeoutMs <= 0) {
      clearTimer()
      return
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      const elapsed = Date.now() - lastActivityRef.current
      if (elapsed >= timeoutMs) {
        logger.warn('Session inactivity timeout reached while tab was hidden')
        onTimeoutRef.current()
        return
      }
      scheduleTimer()
    }

    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, handleActivity, { passive: true })
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    lastActivityRef.current = Date.now()
    scheduleTimer()

    return () => {
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, handleActivity)
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      clearTimer()
    }
  }, [enabled, timeoutMs, handleActivity, scheduleTimer, clearTimer])
}
